import { FunctionComponent, MouseEvent, MouseEventHandler, useState } from "react";
import { orderBy } from '@progress/kendo-data-query';
import { useStatistiquesContext, ViewMode } from "./statistiquesContext";
import { PlanoStatsNiveau } from "../../redux/features/types";
import { Grid, GridColumn, GridCustomCellProps, GridNoRecords, GridPageChangeEvent } from "@progress/kendo-react-grid";
import { Menu, MenuItem } from "@mui/material";


export const StatistiquesGrid: FunctionComponent = () => {
    const {
        niveau,
        currentView,
        magasins,
        divisions,
        sections,
        typeProduitWeb,
        planoStats,
        gridSort,
        setCurrentView,
        setSelectedMagasins,
        setSelectedDivisions,
        setSelectedSections,
        setSelectedTypeProduitWeb,
        setSelectedProducts,
        clearSelectedSections,
        setGridSort,
    } = useStatistiquesContext();

    const [skip, setSkip] = useState(0);
    const pageSize = 100;
    const [contextMenuPosition, setContextMenuPosition] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [contextMenuData, setContextMenuData] = useState<GridCustomCellProps | null>(null);

    const handleContextMenu = (event: MouseEvent<HTMLTableCellElement>, cell: GridCustomCellProps) => {
        if (niveau !== PlanoStatsNiveau.PRODUIT) {
            return;
        }
        event.preventDefault();

        setContextMenuPosition({
            mouseX: event.clientX,
            mouseY: event.clientY,
        });
        setContextMenuData(cell);
    };

    const handleCloseContextMenu = () => {
        setContextMenuPosition(null);
        setContextMenuData(null);
    };

    const contextMenuItems = [
        {
            label: "Inventaire",
            onClick: () => {
                if (!contextMenuData) return;
                const token = localStorage.getItem("token")?.split(" ")[1];
                const user = localStorage.getItem("user");

                const pubURL = process.env.REACT_APP_ENV === "DEV" ? "https://publicite-dev.tanguay.ca" : "https://publicite.tanguay.ca";
                const produit = contextMenuData?.dataItem.txt.split(" ")[0];

                const inventaireURL = `${pubURL}/#/Inventaire/Produit=${produit}?token=${token}&user=${user}`;

                window.open(inventaireURL, "_blank");

                handleCloseContextMenu();
            },
        },
    ];

    const onPageChange = (ev: GridPageChangeEvent) => {
        setSkip(ev.page.skip);
    };

    return (
        <>
            <Grid
                style={{
                    flex: 1,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    overflow: "auto",
                    marginBottom: "1rem",
                    marginRight: "0.5rem",
                    height: "100%",
                    maxHeight: "calc(100% - 24px)",
                }}
                data={orderBy(planoStats, gridSort)}
                onRowClick={(ev) => {
                    const item = ev.dataItem
                    if (currentView === ViewMode.STORE) {
                        const magasin = magasins.find((mag) => mag.mag_nom_aff === item.txt);
                        if (magasin) {
                            setSelectedMagasins(magasin.mag_nom_aff);
                            setCurrentView(ViewMode.PRODUCT);
                        }
                        return;
                    }
                    if (niveau === PlanoStatsNiveau.DIVISION) {
                        const division = divisions?.find((div) => div.div_nom_aff === item.txt);
                        if (division) {
                            setSelectedDivisions(division.div_nom_aff);
                            clearSelectedSections();
                        }
                    } else if (niveau === PlanoStatsNiveau.SECTION) {
                        const section = sections?.find((sec) => sec.plans_nom === item.txt);
                        if (section) {
                            setSelectedSections(section.plans_nom);
                        }
                    } else if (niveau === PlanoStatsNiveau.TYPE_PRODUIT_WEB) {
                        const typeProduitWeb_ = typeProduitWeb?.find((t) => t.tpw_descr_aff === item.txt);
                        if (typeProduitWeb_) {
                            setSelectedTypeProduitWeb(typeProduitWeb_.tpw_cd);
                        }
                    } else if (niveau === PlanoStatsNiveau.PRODUIT) {
                        setSelectedProducts(item.txt);
                        setCurrentView(ViewMode.STORE);
                    }
                }}
                resizable
                reorderable
                sortable={{
                    allowUnsort: true,
                    mode: "single",
                }}
                sort={gridSort}
                onSortChange={(ev) => setGridSort(ev.sort)}
                size="medium"
                total={planoStats.length}
                skip={skip}
                pageSize={pageSize}
                rowHeight={30}
                scrollable="virtual"
                onPageChange={onPageChange}
            >
                <GridNoRecords>
                    Aucun enregistrement disponible.
                </GridNoRecords>
                <GridColumn
                    field={"txt"}
                    title={currentView === ViewMode.STORE ? "Magasin" : niveau === PlanoStatsNiveau.TYPE_PRODUIT_WEB ? "Type de produit" : niveau![0].toUpperCase() + niveau!.slice(1)}
                    resizable={true}
                    reorderable={true}
                    sortable={{
                        allowUnsort: true,
                    }}
                    cells={{
                        data: (props) => (
                            <td
                                onContextMenu={(event) => handleContextMenu(event, props)}
                                style={{
                                    padding: "0.5rem",
                                    boxSizing: "border-box",
                                }}
                            >
                                {props.dataItem.txt}
                            </td>
                        ),
                    }}
                />
                <GridColumn
                    field={"ventesJr"}
                    title="Ventes/Jour"
                    resizable={true}
                    reorderable={true}
                    sortable={{
                        allowUnsort: true,
                    }}
                    cells={{
                        data: (props) => {
                            const nbFormatter = new Intl.NumberFormat('fr-CA', {
                                style: 'decimal',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            });
                            return (
                                (
                                    <td
                                        style={{
                                            padding: "0.5rem",
                                            boxSizing: "border-box",
                                            textAlign: "right",
                                        }}
                                        onContextMenu={(event) => handleContextMenu(event, props)}
                                    >
                                        {nbFormatter.format(props.dataItem.ventesJr)} $
                                    </td>
                                )
                            )
                        }
                    }}
                />
                <GridColumn
                    field={"nbrJrDemo"}
                    title="Nombre de jours de démo moyen"
                    resizable={true}
                    reorderable={true}
                    sortable={{
                        allowUnsort: true,
                    }}
                    cells={{
                        data: (props) => {
                            return (
                                (
                                    <td
                                        style={{
                                            padding: "0.5rem",
                                            boxSizing: "border-box",
                                            textAlign: "right",
                                        }}
                                        onContextMenu={(event) => handleContextMenu(event, props)}
                                    >
                                        {props.dataItem.nbrJrDemo}
                                    </td>
                                )
                            )
                        }
                    }}
                />
                {currentView !== ViewMode.STORE && niveau === PlanoStatsNiveau.PRODUIT && (
                    <GridColumn
                        field={"prix"}
                        title="Prix"
                        resizable={true}
                        reorderable={true}
                        sortable={{
                            allowUnsort: true,
                        }}
                        // width={"100%"}
                        cells={{
                            data: (props) => {
                                const nbFormatter = new Intl.NumberFormat('fr-CA', {
                                    style: 'decimal',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                });
                                return (
                                    (
                                        <td
                                            style={{
                                                padding: "0.5rem",
                                                boxSizing: "border-box",
                                                textAlign: "right",
                                            }}
                                            onContextMenu={(event) => handleContextMenu(event, props)}
                                        >
                                            {nbFormatter.format(props.dataItem.prix)} $
                                        </td>
                                    )
                                )
                            }
                        }}
                    />
                )}
                {currentView !== ViewMode.STORE && niveau === PlanoStatsNiveau.PRODUIT && (
                    <GridColumn
                        field={"couleur"}
                        title="Couleur"
                        resizable={true}
                        reorderable={true}
                        sortable={false}
                        width={"100px"}
                        cells={{
                            data: (props) => {
                                return (
                                    (
                                        <td
                                            style={{
                                                padding: "0.5rem",
                                                boxSizing: "border-box",
                                                textAlign: "right",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                            onContextMenu={(event) => handleContextMenu(event, props)}
                                        >
                                            {props.dataItem.couleur && <div
                                                style={{
                                                    width: "80%",
                                                    height: "20px",
                                                    backgroundColor: props.dataItem.couleur,
                                                    borderRadius: "4px",
                                                    border: "1px solid #484848",
                                                }}
                                            />}
                                        </td>
                                    )
                                )
                            }
                        }}
                    />
                )}
            </Grid>
            <GridContextMenu position={contextMenuPosition} onClose={handleCloseContextMenu} menuItems={contextMenuItems} />
        </>
    );
};

interface GridContextMenuProps {
    position: {
        mouseX: number;
        mouseY: number;
    } | null;
    onClose: () => void;
    menuItems?: { label: string; onClick: () => void }[];
};

const GridContextMenu: FunctionComponent<GridContextMenuProps> = ({ position, onClose, menuItems }) => {
    return (
        <Menu
            open={position !== null}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={
                position !== null
                    ? { top: position.mouseY, left: position.mouseX }
                    : undefined
            }
            aria-hidden={position === null}
        >
            {
                menuItems && menuItems.map((item, index) => (
                    <MenuItem key={index} onClick={item.onClick}>
                        {item.label}
                    </MenuItem>
                ))
            }
        </Menu >
    );
};

export default StatistiquesGrid;